<template>
  <nav
    v-if="totalPages > 1"
    aria-label="Pagination"
    class="flex flex-col items-center gap-2"
  >
    <div class="flex justify-center items-center gap-2">
      <!-- Previous link -->
      <NuxtLink
        v-if="currentPage > 1"
        :to="getPageUrl(currentPage - 1)"
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        :prefetch="true"
      >
        <Icon name="i-heroicons-chevron-left" class="w-4 h-4" />
        Previous
      </NuxtLink>
      <span
        v-else
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed"
      >
        <Icon name="i-heroicons-chevron-left" class="w-4 h-4" />
        Previous
      </span>

      <!-- Page numbers -->
      <div class="hidden sm:flex items-center gap-1">
        <template v-for="page in visiblePages" :key="page">
          <span
            v-if="page === '...'"
            class="px-3 py-2 text-sm text-gray-500"
          >
            ...
          </span>
          <NuxtLink
            v-else-if="page !== currentPage"
            :to="getPageUrl(page as number)"
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            :prefetch="true"
          >
            {{ page }}
          </NuxtLink>
          <span
            v-else
            class="px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-md"
            aria-current="page"
          >
            {{ page }}
          </span>
        </template>
      </div>

      <!-- Mobile: Current page indicator -->
      <span class="sm:hidden text-sm text-gray-600">
        Page {{ currentPage }} of {{ totalPages }}
      </span>

      <!-- Next link -->
      <NuxtLink
        v-if="currentPage < totalPages"
        :to="getPageUrl(currentPage + 1)"
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        :prefetch="true"
      >
        Next
        <Icon name="i-heroicons-chevron-right" class="w-4 h-4" />
      </NuxtLink>
      <span
        v-else
        class="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed"
      >
        Next
        <Icon name="i-heroicons-chevron-right" class="w-4 h-4" />
      </span>
    </div>

    <!-- Results info -->
    <div v-if="showResultsInfo && total" class="text-sm text-gray-600">
      Showing {{ startItem }}-{{ endItem }} of {{ total }} results
    </div>
  </nav>
</template>

<script setup lang="ts">
const props = defineProps<{
  currentPage: number
  totalPages: number
  total?: number
  perPage?: number
  basePath: string
  showResultsInfo?: boolean
}>()

const route = useRoute()

const getPageUrl = (page: number) => {
  const query = { ...route.query }

  if (page === 1) {
    delete query.page
  } else {
    query.page = String(page)
  }

  return {
    path: props.basePath,
    query
  }
}

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const total = props.totalPages
  const current = props.currentPage

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    pages.push(1)

    if (current > 3) {
      pages.push('...')
    }

    const start = Math.max(2, current - 1)
    const end = Math.min(total - 1, current + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (current < total - 2) {
      pages.push('...')
    }

    pages.push(total)
  }

  return pages
})

const startItem = computed(() => {
  if (!props.perPage) return 1
  return (props.currentPage - 1) * props.perPage + 1
})

const endItem = computed(() => {
  if (!props.perPage || !props.total) return props.total || 0
  return Math.min(props.currentPage * props.perPage, props.total)
})
</script>
